import { useEffect, useState } from 'react'
import { Button, Box } from '@mui/material'
import { type GridColDef } from '@mui/x-data-grid'
import { DataTable } from 'components/Shared/DataTable'
import { Modal } from 'components/Modal/Modal'
import { useLayout } from 'hooks/useLayout'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { useGetAllLicensedEstablishments } from 'hooks/api/LicensedEstablishment/useGetAllLicensedEstablishments'
import { type Promotion, type Campaign } from 'types/api'
import { type RequiredId } from 'utils/typeUtils'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useCurrentStateStore } from 'stores/useCurrentStateStore'

interface ViewLocationsModalProps {
  campaign?: RequiredId<Campaign>
  promotion?: RequiredId<Promotion>
  licensedEstablishmentIds?: number[]
  isModalOpen: boolean
  toggleModalIsOpen: () => void
}

interface MappedEstablishment {
  id: number
  name: string
  licenseNumber: string
}

export const ViewLocationsModal = ({
  campaign,
  promotion,
  licensedEstablishmentIds,
  isModalOpen,
  toggleModalIsOpen,
}: ViewLocationsModalProps) => {
  const { isMobile } = useLayout()
  const { currentStateId } = useCurrentStateStore()
  const { data: allLicensedEstablishments, isLoading } =
    useGetAllLicensedEstablishments({
      currentStateId: currentStateId ?? 0,
    })
  const [mappedEstablishments, setMappedEstablishments] = useState<
    MappedEstablishment[]
  >([])
  const heightModal = isMobile ? '100%' : '75%'

  useEffect(() => {
    const entity = campaign ?? promotion
    const entityLicensedEstablishmentIds =
      licensedEstablishmentIds ??
      entity?.licensedEstablishments?.map((le) => le.licensedEstablishmentId)

    if (allLicensedEstablishments) {
      const mappedEstablishments = entityLicensedEstablishmentIds?.map((id) => {
        const matchingEstablishment = Array.isArray(allLicensedEstablishments)
          ? allLicensedEstablishments.find(
              (allLE: { id: number }) => allLE.id === id
            )
          : undefined
        return {
          id,
          name: matchingEstablishment?.standardName
            ? matchingEstablishment.standardName
            : '',
          licenseNumber: matchingEstablishment
            ? matchingEstablishment.licenseNumber
            : '',
        }
      })
      if (mappedEstablishments) {
        setMappedEstablishments(mappedEstablishments)
      }
    }
  }, [allLicensedEstablishments, promotion, campaign, licensedEstablishmentIds])

  const getColumns = (): GridColDef[] => [
    { field: 'name', headerName: 'Name', minWidth: 300, flex: 1 },
    {
      field: 'licenseNumber',
      headerName: 'License Number',
      minWidth: 100,
      maxWidth: 200,
      flex: 1,
    },
  ]

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '55%',
        height: heightModal,
      }}
    >
      <ModalFullHeader title="Locations" toggleIsOpen={toggleModalIsOpen} />
      <ModalFullBody heightModal={heightModal}>
        <Box sx={{ flexGrow: 1, padding: 2 }}>
          {isLoading ? (
            <ActivityIndicator />
          ) : (
            <DataTable
              data-testid="data-table"
              getRowId={(row) => row.licenseNumber}
              sortFieldName={'name'}
              rowHeight={65}
              columns={getColumns()}
              rows={mappedEstablishments}
            />
          )}
        </Box>
      </ModalFullBody>
      <ModalFullFooter>
        <Button variant="text" onClick={toggleModalIsOpen}>
          Close
        </Button>
      </ModalFullFooter>
    </Modal>
  )
}
